import { v4 as uuidv4 } from "uuid";

// Evita ruído de ponto flutuante (ex.: 10 * 0.94 = 9.399999999999999) nos
// campos calculados exibidos ao utilizador.
const round4 = (n) => Math.round(n * 10000) / 10000;

export function timeToMinutes(value) {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return hours * 60 + minutes;
}

// Percorre a árvore de parâmetros (campos + secções "group" aninhadas) e
// devolve só os campos-folha, cada um com o caminho (nomes das secções-pai)
// para uso em validação, inicialização de respostas e relatórios. Templates
// antigos (array plano, sem nenhum nó "group") continuam a funcionar: viram
// folhas com path=[].
export function flattenFields(nodes = [], path = []) {
  const result = [];
  for (const node of nodes || []) {
    if (!node) continue;
    if (node.type === "group") {
      result.push(...flattenFields(node.children, [...path, node.name]));
    } else {
      result.push({ ...node, path });
    }
  }
  return result;
}

// Atribui id/order recursivamente (campos e secções), preservando ids já
// existentes. Secções têm os seus children processados da mesma forma.
export function assignIdsAndOrder(nodes = [], idFactory = uuidv4) {
  return (nodes || []).map((node, index) => {
    const withId = {
      ...node,
      id: node.id || idFactory(),
      order: node.order || index + 1,
      required: node.type === "group" ? node.required : false,
    };
    if (withId.type === "group") {
      withId.children = assignIdsAndOrder(withId.children, idFactory);
    }
    return withId;
  });
}

export function markAllFieldsOptional(nodes = []) {
  return (nodes || []).map((node) => {
    if (!node) return node;
    if (node.type === "group") {
      return {
        ...node,
        children: markAllFieldsOptional(node.children),
      };
    }
    return {
      ...node,
      required: false,
    };
  });
}

// Recalcula todos os campos "calculated" (ex.: TT = TC + TE) a partir das
// respostas atuais, para pré-visualização no formulário. O backend refaz o
// mesmo cálculo antes de persistir, então este valor nunca é a fonte final.
export function computeCalculatedFields(nodes = [], responses = {}) {
  const fields = flattenFields(nodes);
  const result = { ...responses };

  for (const field of fields) {
    if (field.type !== "calculated" || !field.formula) continue;

    const operandIds = field.formula.fields || [];
    const values = operandIds.map((id) => {
      const value = Number(result[id]);
      return Number.isFinite(value) ? value : 0;
    });

    switch (field.formula.op) {
      case "sum":
        result[field.id] = values.reduce((a, b) => a + b, 0);
        break;
      case "multiply":
        result[field.id] =
          values.length > 0 ? round4(values.reduce((a, b) => a * b, 1)) : 0;
        break;
      case "multiply_constant": {
        const constant = Number(field.formula.constant);
        result[field.id] =
          values.length > 0
            ? round4(values[0] * (Number.isFinite(constant) ? constant : 1))
            : 0;
        break;
      }
      case "time_diff_minutes": {
        const [startId, endId] = operandIds;
        const start = timeToMinutes(result[startId]);
        const end = timeToMinutes(result[endId]);
        if (start === null || end === null) {
          result[field.id] = 0;
        } else {
          result[field.id] = end >= start ? end - start : end + 24 * 60 - start;
        }
        break;
      }
      default:
        result[field.id] = null;
    }
  }

  return result;
}
